import "./unlinkedEans.css";
import { useEffect, useState } from "react";
import { Button } from "antd";
import {
  Board,
  BoardBody,
  BoardHead,
  BoardTd,
  BoardTh,
  BoardTr,
  Edit,
  EditSelectionItemProps,
  OperationArea,
  Panel,
} from "../../../components";
import { useApiFetch, useLanguage } from "../../../hooks";
import {
  AdminBrandCandidateProps,
  AdminUnlinkedEanProps,
  AdminUnlinkedEansProps,
  API_URL,
} from "../../../services";
import { formatDateTimeToLocal } from "../../../utils";
import { CategoryInput } from "../users";

const brandLabel = (brand: AdminBrandCandidateProps): string =>
  brand.name ?? brand.email;

// El nombre del producto suele incluir la marca ("SONY PSN PIN FR 10E"): se
// propone la marca cuyo nombre aparece en él. Si coinciden varias, la de nombre
// más largo (la más específica).
const suggestBrand = (
  productName: string | null,
  brands: AdminBrandCandidateProps[]
): AdminBrandCandidateProps | undefined => {
  if (!productName) return undefined;
  const product = ` ${productName.toUpperCase()} `;
  return brands
    .filter((brand) => (brand.name?.trim().length ?? 0) >= 3)
    .filter((brand) => product.includes(` ${brand.name!.trim().toUpperCase()}`))
    .sort((a, b) => b.name!.length - a.name!.length)[0];
};

// Maestro local de marcas: EANs de las operaciones del MDW sin usuario de marca
// vinculado, para vincularlos a mano.
const UnlinkedEansScreen: React.FC = () => {
  const { t } = useLanguage();
  const s = t.adminUnlinkedEansScreen;

  const [fetchData, data, loading] = useApiFetch<AdminUnlinkedEansProps>({
    eans: [],
    brands: [],
  });
  const [fetchLink, , linking, linkError] = useApiFetch<{
    userId: number;
    linked: string[];
  }>();

  // Marca elegida a mano por EAN; si no hay, se usa la propuesta.
  const [chosen, setChosen] = useState<Record<string, string>>({});
  // Categoría escrita por EAN (solo se puede escribir con una marca elegida).
  const [categories, setCategories] = useState<Record<string, string>>({});
  const [lastLinked, setLastLinked] = useState<string | null>(null);

  const load = async () => {
    await fetchData(API_URL.ADMIN_UNLINKED_EANS(), { method: "GET" });
  };

  useEffect(() => {
    load();
  }, []);

  const brands = data?.brands ?? [];
  const eans = data?.eans ?? [];
  const brandItems: EditSelectionItemProps[] = brands.map((brand) => ({
    key: String(brand.id),
    text: brandLabel(brand),
  }));

  const selectedBrandId = (row: AdminUnlinkedEanProps): string | undefined =>
    row.ean in chosen
      ? chosen[row.ean] || undefined
      : suggestBrand(row.productName, brands)?.id.toString();

  const handleLink = async (row: AdminUnlinkedEanProps) => {
    const brandId = selectedBrandId(row);
    if (!brandId) return;

    const result = await fetchLink(API_URL.ADMIN_UNLINKED_EANS("/link"), {
      method: "POST",
      body: JSON.stringify({
        userId: Number(brandId),
        eans: [row.ean],
        category: categories[row.ean] ?? "",
      }),
    });
    if (!result?.data) return;

    const brand = brands.find((b) => String(b.id) === brandId);
    setLastLinked(`${row.ean} ${s.linked} ${brand ? brandLabel(brand) : ""}`);
    await load();
  };

  return (
    <Panel>
      <OperationArea title={s.title}>
        <p className="unlinked-description">{s.description}</p>
        {brands.length === 0 && !loading && (
          <p className="unlinked-warning">{s.noBrands}</p>
        )}
        {lastLinked && <p className="unlinked-result">{lastLinked}</p>}
        {linkError && <p className="unlinked-error">{linkError}</p>}

        <Board id="table-admin-unlinked-eans">
          <BoardHead>
            <BoardTr>
              <BoardTh text={s.table.thead.ean} />
              <BoardTh text={s.table.thead.productName} />
              <BoardTh text={s.table.thead.idProd} />
              <BoardTh
                text={s.table.thead.operations}
                className="board-align-right"
              />
              <BoardTh text={s.table.thead.lastDate} />
              <BoardTh text={s.table.thead.brand} />
              <BoardTh text={s.table.thead.category} />
              <BoardTh text={s.table.thead.actions} />
            </BoardTr>
          </BoardHead>

          <BoardBody isLoading={loading}>
            {eans.length === 0 && !loading ? (
              <BoardTr>
                <BoardTd>
                  <p>{s.table.empty}</p>
                </BoardTd>
              </BoardTr>
            ) : (
              eans.map((row) => {
                const brandId = selectedBrandId(row);
                const isSuggestion = !(row.ean in chosen) && !!brandId;
                const selected = brandItems.find(
                  (item) => item.key === brandId
                ) ?? { key: "", text: "" };

                return (
                  <BoardTr key={`unlinked-ean-${row.ean}`}>
                    <BoardTd>
                      <p className="board-bold">{row.ean}</p>
                    </BoardTd>
                    <BoardTd hasClassNameW100>
                      <p>{row.productName}</p>
                    </BoardTd>
                    <BoardTd>
                      <p>{row.idProd}</p>
                    </BoardTd>
                    <BoardTd className="board-align-right">
                      <p>{row.operations}</p>
                    </BoardTd>
                    <BoardTd>
                      <p>{formatDateTimeToLocal(row.lastDate)}</p>
                    </BoardTd>
                    <BoardTd hasClassNameDirection={false}>
                      <div
                        className={`unlinked-brand ${
                          isSuggestion ? "suggested" : ""
                        }`}
                        title={isSuggestion ? s.suggested : undefined}
                      >
                        <Edit.Selection
                          show
                          id={`unlinked-brand-${row.ean}`}
                          placeholder={s.selectBrand}
                          items={brandItems}
                          selected={selected}
                          onSelect={(item) =>
                            setChosen((prev) => ({
                              ...prev,
                              [row.ean]: item.key,
                            }))
                          }
                        />
                      </div>
                    </BoardTd>
                    <BoardTd hasClassNameDirection={false}>
                      <div className="unlinked-category">
                        <CategoryInput
                          value={categories[row.ean] ?? ""}
                          categories={
                            brands.find((b) => String(b.id) === brandId)
                              ?.categories ?? []
                          }
                          placeholder={s.categoryPlaceholder}
                          disabled={!brandId}
                          onChange={(category) =>
                            setCategories((prev) => ({
                              ...prev,
                              [row.ean]: category,
                            }))
                          }
                        />
                      </div>
                    </BoardTd>
                    <BoardTd hasClassNameDirection={false}>
                      <Button
                        type="primary"
                        size="small"
                        disabled={!brandId || linking}
                        onClick={() => handleLink(row)}
                      >
                        {s.link}
                      </Button>
                    </BoardTd>
                  </BoardTr>
                );
              })
            )}
          </BoardBody>
        </Board>
      </OperationArea>
    </Panel>
  );
};

export default UnlinkedEansScreen;
